import React from 'react';

import {UserAvatar} from 'app/components/general/avatars';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {UserActionsButton} from 'app/components/general/UserActionsButton';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {Tags} from 'app/procedures/components/Tags';
import {useAccountSettings} from 'app/settings/api';
import {
  buildTeamListWithUserPermission,
  getUserPermDescription,
  TeamName,
} from 'app/teams/components/TeamDetailPage/TeamMembersSubPage/TeamMemberListItem';
import {StyledTeamListItem} from 'app/teams/components/TeamItem';
import {UserStatusesBadges} from 'app/user/components/UserStatusesBadges';
import {gt} from 'app/utils/i18n';

import type {ActionsContextType} from 'app/components/general/UserActionsButton';
import type {UserSerializedType} from 'sweet-types';

interface Props {
  user: UserSerializedType;
  pageContext: ActionsContextType;
  enableBulkSelect?: boolean;
  isSelected?: boolean;
  addToSelection?: (userToAdd: UserSerializedType) => unknown;
  removeFromSelection?: (userToRemove: UserSerializedType) => unknown;
}

export function UserListItem({
  user,
  pageContext,
  enableBulkSelect = false,
  isSelected = false,
  addToSelection,
  removeFromSelection,
}: Props): JSX.Element {
  const {data: settings} = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  });

  return (
    <StyledTeamListItem
      className={`${user.is_active === false ? 'inactive' : ''}`}
    >
      <div className="col-12 col-lg d-flex align-items-center">
        {enableBulkSelect && (
          <CheckboxButton
            isChecked={isSelected}
            onClick={(isChecked: boolean) =>
              isChecked ? addToSelection?.(user) : removeFromSelection?.(user)
            }
            useRealCheckbox={true}
            className="mr-2 btn btn-sm btn-link"
          />
        )}
        <UserAvatar
          className="mr-2"
          user={user}
          tooltipPlacement={'right'}
          isGuest={user.is_guest_until != null}
        />
        <FullHTMLLink
          html_url={user.html_url}
          title={user.email}
          className="text-dark font-weight-bold"
        >
          {user.name || user.email || gt('Unnamed Person')}{' '}
          {getUserPermDescription(user, settings?.owner_email)}
          <UserStatusesBadges user={user} />
        </FullHTMLLink>
      </div>

      <div className="col-11 col-lg text-truncate d-flex align-items-center">
        <div className="text-truncate">
          <Tags
            items={buildTeamListWithUserPermission(user)}
            getName={TeamName}
          />
        </div>
      </div>

      <div className="col-1 col-sm-auto ml-auto pl-0 d-flex align-items-start align-items-sm-center flex-row flex-column-reverse flex-sm-row">
        <UserActionsButton user={user} pageContext={pageContext} />
      </div>
    </StyledTeamListItem>
  );
}
